import {
  FC,
  useCallback,
  MouseEventHandler,
  useEffect,
  useRef,
  useLayoutEffect,
  useState,
  useMemo,
} from 'react';
import { useDrag, useDrop, DragSourceMonitor } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  PlaylistItem as Item,
  Playlist,
  Profile,
} from '@raydiant/api-client-js';
import { isCycle } from '../../../usePlaylistPageState/utilities';
import {
  canEditResource,
  canReadResource,
} from '../../../../../utilities/permissions';
import { getDuplicateResourceName } from '../../../../../utilities';
import useCurrentUser from '../../../../../hooks/useCurrentUser';
import useCopyPlaylist from '../../../../../hooks/useCopyPlaylist';
import useCopyPresentation from '../../../../../hooks/useCopyPresentation';
import { useToastState } from '@raydiant/raydial/components/Toast';
import analytics from '../../../../../clients/analyticsClient';
import { ItemIDPath, PlaylistIDPath } from '../../../playlistPageTypes';
import PlaylistItemPlaylistV2 from './PlaylistItemPlaylistV2';
import PlaylistItemPresentationV2 from './PlaylistItemPresentationV2';
import usePlaylistPageContext from '../../../usePlaylistPageContext';
import createPlaylistItemDragItem, {
  PlaylistItemDragItem,
} from '../../../createPlaylistItemDragItem';
import { createNewId, isNewId } from '../../../../../utilities/identifiers';
import RenamePlaylistDialog from '../RenamePlaylistDialog/RenamePlaylistDialog';
import PlaylistScheduleModalV2 from '../PlaylistScheduleModalV2/PlaylistScheduleModalV2';
import { useScheduleVisibility } from './ScheduleVisibilityContext';
import { selectIsSeeInLibraryEnabled } from '../../../../../selectors/user';
import * as paths from '../../../../../routes/paths';

interface PlaylistItemV2Props {
  item: Item;
  itemIdPath: ItemIDPath;
  parentPlaylist: Playlist;
  playlistIdPath: PlaylistIDPath;
  className?: string;
}

const canDrop = (
  currentUser: Profile,
  item: Item,
  parentPlaylist: Playlist,
  dropPosition: string,
) => {
  if (currentUser) {
    if (dropPosition === 'center') {
      const itemResource =
        item.presentation?.resource ?? item.playlist?.resource;

      if (itemResource) {
        return canEditResource(currentUser, itemResource);
      }
    } else {
      return canEditResource(currentUser, parentPlaylist.resource);
    }
  }
  return false;
};

const canDrag = (currentUser: Profile, item: Item) => {
  if (currentUser) {
    const itemResource = item.presentation?.resource ?? item.playlist?.resource;

    if (itemResource) {
      return canEditResource(currentUser, itemResource);
    }
  }
  return false;
};

const isScheduleCompleted = (playlist: Playlist) => {
  if (!playlist.startDatetime || !playlist.endDatetime) return false;
  if (playlist.recurrenceRule) return false;
  const endMs = Date.parse(playlist.endDatetime);
  if (Number.isNaN(endMs)) return false;
  return endMs < Date.now();
};

const PlaylistItemV2: FC<PlaylistItemV2Props> = ({
  item,
  itemIdPath,
  playlistIdPath,
  parentPlaylist,
  className,
}) => {
  const history = useHistory();

  const {
    queryParams,
    getPageUrl,
    toggleExpanded,
    isExpanded,
    toggleSelected,
    setInitialSelected,
    setSelectedEnd,
    isSelected,
    getSelectedItems,
    moveSelectedItems,
    setPreviewItem,
    isNameEditable,
    getPlaylist,
    getPresentation,
    editPresentation,
    openModal,
    updatePlaylist,
    addPlaylistItems,
    removePlaylistItem,
    isUploading,
    isRecentlyAdded,
  } = usePlaylistPageContext();

  // Queries

  const { data: currentUser } = useCurrentUser();

  const toast = useToastState();

  // State

  const [dropPosition, setDropPosition] = useState<
    'top' | 'bottom' | 'center' | null
  >(null);

  const itemRef = useRef<HTMLDivElement | null>(null);
  // Resolve latest entities from page state so permissions work immediately for
  // newly added items whose full data arrives asynchronously.
  const itemPlaylist = item.playlistId
    ? getPlaylist(item.playlistId) ?? item.playlist
    : undefined;
  const itemPresentation = item.presentationId
    ? getPresentation(item.presentationId) ?? item.presentation
    : undefined;
  const itemWithLatestData = useMemo(
    () => ({
      ...item,
      playlist: itemPlaylist ?? null,
      presentation: itemPresentation ?? null,
    }),
    [item, itemPlaylist, itemPresentation],
  );

  // DnD

  const [{ isDragging }, connectDragSource, dragPreview] = useDrag<
    PlaylistItemDragItem,
    void,
    { isDragging: boolean }
  >({
    item: createPlaylistItemDragItem(),

    begin: () => {
      if (!itemRef.current) {
        return createPlaylistItemDragItem();
      }

      // If current item isn't selected, assume user is starting a new selection
      // and de-select any existing items.
      const shouldResetSelection = !isSelected(itemIdPath);
      if (shouldResetSelection) {
        setInitialSelected(itemIdPath);
      }

      // Selected state isn't updated until after this callback runs so we need to
      // empty the drag stack if we are reseting the selection.
      const stack = shouldResetSelection
        ? [item]
        : [item, ...getSelectedItems(itemIdPath)];

      const { x, y, width } = itemRef.current.getBoundingClientRect();

      return createPlaylistItemDragItem(stack, x, y, width);
    },

    collect: (monitor: DragSourceMonitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [{ isOver }, connectDropSource] = useDrop<
    PlaylistItemDragItem,
    void,
    { isOver: boolean }
  >({
    accept: 'PlaylistItem',

    drop: () => {
      if (!isOver || !dropPosition) return;
      moveSelectedItems(itemIdPath, dropPosition);
    },

    hover: (_, monitor) => {
      if (!itemRef.current) return;

      const itemRect = itemRef.current.getBoundingClientRect();
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;

      const topEnd =
        itemRect.y + itemRect.height * (item.playlistId ? 0.33 : 0.5);
      const bottomStart =
        itemRect.y + itemRect.height * (item.playlistId ? 0.66 : 0.5);

      // Calculate the drop position based on the hover coordinates.
      if (
        clientOffset.y < topEnd &&
        currentUser &&
        canDrop(currentUser, itemWithLatestData, parentPlaylist, 'top')
      ) {
        setDropPosition('top');
      } else if (
        clientOffset.y < bottomStart &&
        currentUser &&
        canDrop(currentUser, itemWithLatestData, parentPlaylist, 'center')
      ) {
        setDropPosition('center');
      } else if (
        currentUser &&
        canDrop(currentUser, itemWithLatestData, parentPlaylist, 'bottom')
      ) {
        setDropPosition('bottom');
      } else {
        setDropPosition(null);
      }
    },

    collect: (monitor) => {
      return {
        isOver: monitor.isOver({ shallow: true }),
      };
    },

    canDrop: () => {
      return !!dropPosition;
    },
  });

  // Callbacks

  const toggleExpandedItem = useCallback<MouseEventHandler>(
    (event) => {
      if (!item.playlistId) return;
      // Prevent main onClick handler from running
      event.stopPropagation();
      // Show / hide playlist items
      toggleExpanded(itemIdPath, item.playlistId);
    },
    [item.playlistId, itemIdPath, toggleExpanded],
  );

  const [scheduleModalPlaylist, setScheduleModalPlaylist] =
    useState<Playlist | null>(null);

  const showSchedule = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (event) => {
      event.stopPropagation();
      if (!item.playlistId) return;
      const playlist = getPlaylist(item.playlistId) ?? item.playlist;
      if (!playlist) return;
      analytics.trackPlaylistScheduleOpened({ playlistId: playlist.id });
      setScheduleModalPlaylist(playlist);
    },
    [item, getPlaylist],
  );

  const handleClick = useCallback<MouseEventHandler>(
    (event) => {
      if (event.shiftKey) {
        // Shift-click, select items between initial click.
        setSelectedEnd(itemIdPath);
      } else if (event.metaKey) {
        // Cmd-click, toggle item in selected list.
        toggleSelected(itemIdPath);
      } else if (isSelected(itemIdPath)) {
        // Click on already selected item → deselect and clear preview.
        toggleSelected(itemIdPath);
        setPreviewItem(null);
      } else {
        // Reset selected list and toggle selected item.
        setInitialSelected(itemIdPath);
        // Set the preview item.
        setPreviewItem(item);
      }
    },
    [
      item,
      itemIdPath,
      setSelectedEnd,
      toggleSelected,
      setInitialSelected,
      setPreviewItem,
      isSelected,
    ],
  );

  const handleEditClick = useCallback<MouseEventHandler>(
    (event) => {
      event.stopPropagation();
      if (item.presentationId) {
        editPresentation(item.presentationId);
      } else if (item.playlistId) {
        const targetPlaylistId = itemPlaylist?.id ?? item.playlistId;
        if (!targetPlaylistId || isNewId(targetPlaylistId)) {
          toast.add({
            title: 'Save required',
            description:
              'Save this playlist before editing a newly created nested playlist.',
            variant: 'caution',
          });
          return;
        }

        const pageUrl = getPageUrl(queryParams);
        history.push(
          paths.editPlaylist(targetPlaylistId, {
            backTo: pageUrl,
            backToLabel: `Back to playlist`,
            saveTo: pageUrl,
            // Don't pass parent's sessionId - let editPlaylist generate a new one
            // so the nested playlist page fetches its own data fresh.
          }),
        );
      }
    },
    [
      item,
      itemPlaylist,
      editPresentation,
      history,
      queryParams,
      getPageUrl,
      toast,
    ],
  );

  // Playlist-specific action handlers
  const [renameOpen, setRenameOpen] = useState(false);
  const { mutateAsync: copyPlaylist } = useCopyPlaylist();

  const handleRenameClick = useCallback(() => {
    setRenameOpen(true);
  }, []);

  const handleRenameSubmit = useCallback(
    (newName: string) => {
      if (!item.playlistId) return;
      updatePlaylist(item.playlistId, { name: newName });
      setRenameOpen(false);
    },
    [item.playlistId, updatePlaylist],
  );

  const handleRulesClick = useCallback(() => {
    if (!item || !itemIdPath) return;
    analytics.trackPlaylistRulesOpened({
      playlistId: item.playlistId || '',
    });
    openModal(item, itemIdPath, 'rules');
  }, [item, itemIdPath, openModal]);

  const handleTagsClick = useCallback(() => {
    if (!item || !itemIdPath) return;
    analytics.trackPlaylistTagsOpened({
      playlistId: item.playlistId || '',
    });
    openModal(item, itemIdPath, 'tags');
  }, [item, itemIdPath, openModal]);

  const handleDuplicateClick = useCallback(async () => {
    if (!item.playlist) return;
    const targetPlaylistId = itemPlaylist?.id ?? item.playlistId;
    if (!targetPlaylistId || isNewId(targetPlaylistId)) {
      toast.add({
        title: 'Save required',
        description: 'Save changes before duplicating this playlist.',
        variant: 'caution',
      });
      return;
    }
    analytics.trackPlaylistDuplicateClicked({
      playlistId: item.playlistId || '',
    });

    try {
      const parentResources: Array<any> = [];
      for (const pi of parentPlaylist.items) {
        if (pi.playlist) parentResources.push(pi.playlist);
        if (pi.presentation) parentResources.push(pi.presentation);
      }

      const newPlaylist = await copyPlaylist({
        playlistId: item.playlist.id,
        params: {
          name: getDuplicateResourceName(item.playlist.name, parentResources),
        },
      });

      addPlaylistItems(itemIdPath, [
        {
          id: createNewId(),
          playlistId: newPlaylist.id,
          playlist: newPlaylist,
          presentationId: null,
        },
      ]);

      toast.add({
        title: 'Playlist duplicated',
        description: 'Playlist duplicated successfully',
      });
    } catch {
      toast.add({
        title: 'Something went wrong',
        description: 'Please try again',
        variant: 'caution',
      });
    }
  }, [
    item,
    itemPlaylist,
    itemIdPath,
    parentPlaylist,
    copyPlaylist,
    addPlaylistItems,
    toast,
  ]);

  const handleRemoveClick = useCallback(() => {
    removePlaylistItem(itemIdPath);
  }, [itemIdPath, removePlaylistItem]);

  const isSeeInLibraryEnabled = useSelector(selectIsSeeInLibraryEnabled);

  // See in Library only routes to My Library for now, so it's hidden for
  // content the current profile doesn't own — it would land in an empty
  // folder. Shared (CX-4995) and users (CX-4996) library routing will lift
  // this. Known gap until CX-4995: owned content moved into a Shared Library
  // still routes to My Library since the playlist response can't tell which
  // library tree the parent folder belongs to.
  const seeInLibraryResource =
    itemPresentation?.resource ?? itemPlaylist?.resource;
  const showSeeInLibrary =
    isSeeInLibraryEnabled &&
    !!currentUser &&
    seeInLibraryResource?.profile.id === currentUser.id;

  const handleSeeInLibraryClick = useCallback(() => {
    const resource = itemPresentation?.resource ?? itemPlaylist?.resource;
    if (!resource) return;
    // S1a: navigate to My Library at the content's folder and flag it for
    // highlight (the highlight itself is consumed in S1b/CX-4993). Detection for
    // shared (CX-4995) and admin (CX-4996) content routes to the Shared / Users
    // Library tabs and lands in those slices; until then every item falls
    // through to My Library so the action is never a dead click while the
    // feature flag is on.
    // back_to is the current playlist editor URL so the library back control
    // can return here; back_to_folder is the landing folder (the content's
    // parent), which the library treats as the back-to-playlist boundary.
    const currentUrl = `${history.location.pathname}${history.location.search}`;
    history.push(
      paths.seeInLibrary({
        folderId: resource.parentFolderId,
        highlightResourceId: resource.id,
        backTo: currentUrl,
        backToFolder: resource.parentFolderId,
      }),
    );
  }, [itemPresentation, itemPlaylist, history]);

  const showRulesAndTags = !!currentUser?.domainId;
  const shouldHidePlaylist = useScheduleVisibility();

  // Presentation-specific action handlers
  const { mutateAsync: copyPresentation } = useCopyPresentation();
  const handlePresentationTagsClick = useCallback(() => {
    if (!item || !itemIdPath) return;
    openModal(item, itemIdPath, 'tags');
  }, [item, itemIdPath, openModal]);

  const handlePresentationDuplicateClick = useCallback(async () => {
    if (!item.presentation) return;
    analytics.trackPlaylistDuplicateClicked({
      playlistId: item.playlistId || '',
    });

    try {
      const parentResources: Array<any> = [];
      for (const pi of parentPlaylist.items) {
        if (pi.playlist) parentResources.push(pi.playlist);
        if (pi.presentation) parentResources.push(pi.presentation);
      }

      const newPresentation = await copyPresentation({
        presentationId: item.presentation.id,
        params: {
          name: getDuplicateResourceName(
            item.presentation.name,
            parentResources,
          ),
        },
      });

      if (
        currentUser &&
        canEditResource(currentUser, parentPlaylist.resource)
      ) {
        addPlaylistItems(itemIdPath, [
          {
            id: createNewId(),
            presentationId: newPresentation.id,
            presentation: newPresentation,
            playlistId: null,
          },
        ]);
      }

      toast.add({
        title: 'Presentation duplicated',
        description: 'Presentation duplicated successfully',
      });
    } catch {
      toast.add({
        title: 'Something went wrong',
        description: 'Please try again',
        variant: 'caution',
      });
    }
  }, [
    item,
    itemIdPath,
    parentPlaylist,
    currentUser,
    copyPresentation,
    addPlaylistItems,
    toast,
  ]);

  // Disable the HTML5 drag preview image because we are rendering our
  // own custom drag layer.
  useEffect(() => {
    dragPreview(getEmptyImage(), { captureDraggingState: true });
  }, [dragPreview]);

  useLayoutEffect(() => {
    if (itemRef.current && currentUser) {
      if (canDrag(currentUser, itemWithLatestData)) {
        connectDragSource(itemRef.current);
      }
      connectDropSource(itemRef.current);
    }
  }, [connectDragSource, connectDropSource, currentUser, itemWithLatestData]);

  // Render

  const indent = Math.max(itemIdPath.length - 1, 0);
  const selected = isDragging || isSelected(itemIdPath);

  // Check if this item is currently uploading or recently added
  const itemIsUploading = isUploading(item.id);
  const itemIsRecentlyAdded = isRecentlyAdded(item.id);

  // Show uploading state when item is being uploaded
  if (itemIsUploading && !itemPresentation && !itemPlaylist) {
    return (
      <PlaylistItemPresentationV2
        ref={itemRef}
        presentation={
          {
            id: item.id,
            name: 'uploading...',
            applicationId: '',
            applicationVariables: {},
            resource: { r: { tags: [] } },
          } as any
        }
        indent={indent}
        selected={selected}
        disabled={true}
        isUploading={true}
        className={className}
      />
    );
  }

  if (currentUser && itemPlaylist) {
    const hasSchedule = !!itemPlaylist.startDatetime;
    const hasCompletedSchedule = isScheduleCompleted(itemPlaylist);
    const shouldHideCompletedAutoRemoveItem = shouldHidePlaylist(itemPlaylist);

    // Server-side cleanup runs on a schedule. Hide completed auto-remove items
    // in the UI to avoid showing stale entries before cleanup runs.
    if (shouldHideCompletedAutoRemoveItem) {
      return null;
    }

    const cyclic = isCycle(itemPlaylist.id, playlistIdPath);

    const canEdit = canEditResource(currentUser, itemPlaylist.resource);
    const isUnsavedNestedPlaylist = isNewId(itemPlaylist.id);

    return (
      <>
        <PlaylistItemPlaylistV2
          ref={itemRef}
          playlist={itemPlaylist}
          indent={indent}
          expanded={isExpanded(itemIdPath)}
          cyclic={cyclic}
          selected={cyclic ? false : selected}
          disabled={!canEdit}
          dropPosition={isOver && dropPosition ? dropPosition : undefined}
          editable={isNameEditable(itemIdPath)}
          itemIdPath={itemIdPath}
          playlistIdPath={playlistIdPath}
          onClick={cyclic ? undefined : handleClick}
          onEditClick={canEdit ? handleEditClick : undefined}
          editDisabled={canEdit && isUnsavedNestedPlaylist}
          editDisabledTooltip="Save changes before editing this playlist"
          hasSchedule={hasSchedule}
          hasCompletedSchedule={hasCompletedSchedule}
          onScheduleClick={showSchedule}
          onToggleExpandClick={toggleExpandedItem}
          onRenameClick={canEdit ? handleRenameClick : undefined}
          onRulesClick={handleRulesClick}
          onTagsClick={handleTagsClick}
          onDuplicateClick={handleDuplicateClick}
          duplicateDisabled={isUnsavedNestedPlaylist}
          duplicateDisabledTooltip="Save changes before duplicating this playlist"
          onSeeInLibraryClick={
            showSeeInLibrary ? handleSeeInLibraryClick : undefined
          }
          seeInLibraryDisabled={showSeeInLibrary && isUnsavedNestedPlaylist}
          onRemoveClick={canEdit ? handleRemoveClick : undefined}
          showRulesAndTags={showRulesAndTags}
          hideActions={!canReadResource(currentUser, itemPlaylist.resource)}
          className={className}
          isRecentlyAdded={itemIsRecentlyAdded}
        />

        <RenamePlaylistDialog
          isOpen={renameOpen}
          currentName={itemPlaylist.name}
          onSubmit={handleRenameSubmit}
          onClose={() => setRenameOpen(false)}
        />

        {scheduleModalPlaylist && (
          <PlaylistScheduleModalV2
            isOpen
            playlist={scheduleModalPlaylist}
            onClose={() => setScheduleModalPlaylist(null)}
          />
        )}
      </>
    );
  }

  if (currentUser && itemPresentation) {
    return (
      <PlaylistItemPresentationV2
        ref={itemRef}
        presentation={itemPresentation}
        indent={indent}
        selected={selected}
        disabled={!canEditResource(currentUser, itemPresentation.resource)}
        dropPosition={
          isOver && dropPosition && dropPosition !== 'center'
            ? dropPosition
            : undefined
        }
        onClick={handleClick}
        onEditClick={
          canEditResource(currentUser, itemPresentation.resource)
            ? handleEditClick
            : undefined
        }
        onTagsClick={handlePresentationTagsClick}
        onDuplicateClick={handlePresentationDuplicateClick}
        onSeeInLibraryClick={
          showSeeInLibrary ? handleSeeInLibraryClick : undefined
        }
        seeInLibraryDisabled={showSeeInLibrary && isNewId(itemPresentation.id)}
        onRemoveClick={handleRemoveClick}
        showRulesAndTags={showRulesAndTags}
        hideActions={!canReadResource(currentUser, itemPresentation.resource)}
        className={className}
        isRecentlyAdded={itemIsRecentlyAdded}
      />
    );
  }

  return null;
};

export default PlaylistItemV2;
